import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '@/App';
import {
  LayoutDashboard, AlertCircle, BookOpen, BarChart3,
  LogOut, Settings, ChevronRight, Zap, Activity
} from 'lucide-react';

const NAV = [
  { to: "/operations",              icon: LayoutDashboard, label: "Dashboard",       end: true },
  { to: "/operations/incidents",    icon: AlertCircle,     label: "Incidents"              },
  { to: "/operations/knowledge",    icon: BookOpen,        label: "Knowledge Review"       },
  { to: "/operations/executive",    icon: BarChart3,       label: "Executive"              },
  { to: "/operations/pilot-health", icon: Activity,        label: "Pilot Health"           },
];

export default function OperationsLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => { await logout(); navigate("/auth"); };

  return (
    <div className="flex h-screen bg-background overflow-hidden">
      {/* Sidebar */}
      <aside className="w-56 flex-shrink-0 border-r border-border bg-card flex flex-col">
        {/* Brand */}
        <div className="px-4 py-4 border-b border-border">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-md bg-primary flex items-center justify-center">
              <Zap className="w-4 h-4 text-primary-foreground" />
            </div>
            <div>
              <div className="text-sm font-semibold text-foreground leading-tight">QueryVerse</div>
              <div className="text-[10px] text-muted-foreground">Operations</div>
            </div>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-2 py-3 space-y-0.5">
          {NAV.map(({ to, icon: Icon, label, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                `flex items-center gap-2.5 px-3 py-2 rounded-md text-sm transition-colors ${
                  isActive
                    ? "bg-primary/10 text-primary font-medium"
                    : "text-muted-foreground hover:bg-accent hover:text-foreground"
                }`
              }
            >
              <Icon className="w-4 h-4 flex-shrink-0" />
              {label}
            </NavLink>
          ))}
        </nav>

        {/* Footer */}
        <div className="px-2 py-3 border-t border-border space-y-0.5">
          <NavLink
            to="/knowledge-base"
            className="flex items-center gap-2.5 px-3 py-2 rounded-md text-sm text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
          >
            <Settings className="w-4 h-4" />
            Knowledge Base
          </NavLink>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-sm text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
          >
            <LogOut className="w-4 h-4" />
            Sign out
          </button>
          {user && (
            <div className="px-3 pt-2">
              <div className="text-xs text-muted-foreground truncate">{user.email}</div>
            </div>
          )}
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 overflow-y-auto">
        {children}
      </main>
    </div>
  );
}

/** Reusable page header with optional breadcrumb */
export function PageHeader({ title, subtitle, breadcrumb, actions }) {
  return (
    <div className="border-b border-border bg-card px-6 py-4">
      {breadcrumb && (
        <div className="flex items-center gap-1 text-xs text-muted-foreground mb-1">
          {breadcrumb.map((b, i) => (
            <React.Fragment key={i}>
              {i > 0 && <ChevronRight className="w-3 h-3" />}
              <span className={i === breadcrumb.length - 1 ? "text-foreground" : ""}>{b}</span>
            </React.Fragment>
          ))}
        </div>
      )}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold text-foreground">{title}</h1>
          {subtitle && <p className="text-sm text-muted-foreground mt-0.5">{subtitle}</p>}
        </div>
        {actions && <div className="flex items-center gap-2">{actions}</div>}
      </div>
    </div>
  );
}

/** Severity badge */
export function SeverityBadge({ severity }) {
  const map = {
    critical: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
    high:     "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400",
    medium:   "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400",
    low:      "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${map[severity] || map.medium}`}>
      {severity}
    </span>
  );
}

/** Status badge */
export function StatusBadge({ status }) {
  const map = {
    open:          "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
    investigating: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400",
    closed:        "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400",
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${map[status] || map.open}`}>
      {status}
    </span>
  );
}

/** KPI card */
export function KpiCard({ label, value, sub, color = "default" }) {
  const colors = {
    default: "text-foreground",
    green:   "text-green-600 dark:text-green-400",
    red:     "text-red-600 dark:text-red-400",
    amber:   "text-amber-600 dark:text-amber-400",
    blue:    "text-blue-600 dark:text-blue-400",
  };
  return (
    <div className="bg-card border border-border rounded-lg px-4 py-3">
      <div className="text-xs text-muted-foreground mb-1">{label}</div>
      <div className={`text-2xl font-bold ${colors[color]}`}>{value ?? "—"}</div>
      {sub && <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}
